import * as React from "react";
import { LocalAudioTrack, LocalVideoTrack, Room as TwilioRoom, TwilioError } from "twilio-video";
import { useAutoJoinRoom } from "../twilio/hooks/useAutoJoinRoom";
import { useHandleRoomDisconnection } from "../twilio/hooks/useHandleRoomDisconnection";
import { useLocalTracks } from "../twilio/hooks/useLocalTracks";
import { useTwilioRoom } from "../twilio/hooks/useTwilioRoom";

interface ContextValue {
	room: TwilioRoom | undefined;
	isConnecting: boolean;
	localTracks: (LocalVideoTrack | LocalAudioTrack)[];
	getLocalTracks: () => void;
	getLocalAudioTrack: () => Promise<LocalAudioTrack>;
	getLocalVideoTrack: () => Promise<LocalVideoTrack>;
}

const ctx = React.createContext<ContextValue | undefined>(undefined);

interface Props {
	roomId: string;
	token: string | undefined;
	acquireLocalTracks: boolean;
	isEnabled: boolean;
}

function onError(err: TwilioError) {
	console.error(err);
}

export const TwilioProvider: React.FC<Props> = ({
	roomId,
	token,
	acquireLocalTracks = true,
	isEnabled = true,
	children,
}) => {
	const {
		getAudioAndVideoTracks,
		getLocalAudioTrack,
		getLocalVideoTrack,
		removeLocalAudioTrack,
		removeLocalVideoTrack,
		localTracks,
	} = useLocalTracks();
	const { twilioRoom, connectTwilio, isConnecting } = useTwilioRoom(onError);

	useHandleRoomDisconnection(twilioRoom || null, removeLocalAudioTrack, removeLocalVideoTrack, onError);
	useAutoJoinRoom(
		roomId,
		token,
		twilioRoom || null,
		getAudioAndVideoTracks,
		connectTwilio,
		acquireLocalTracks,
		isEnabled,
		onError,
	);
	const value = React.useMemo<ContextValue>(() => {
		return {
			room: twilioRoom,
			getLocalTracks: getAudioAndVideoTracks,
			getLocalAudioTrack,
			getLocalVideoTrack,
			isConnecting,
			localTracks,
		};
	}, [getAudioAndVideoTracks, getLocalAudioTrack, getLocalVideoTrack, isConnecting, localTracks, twilioRoom]);

	if (!twilioRoom) {
		return null;
	}

	return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useTwilioContext = () => {
	const context = React.useContext(ctx);
	if (!context) {
		throw new Error("useTwilioContext");
	}

	return context;
};
